import { useState } from 'react';
import { useLanguage } from './i18n/LanguageContext';

function Contact() {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    enquiryType: '',
    message: ''
  });
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState('next');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const formSteps = [
    {
      field: 'name',
      label: t.contact.steps.name,
      type: 'text',
      placeholder: t.contact.steps.namePlaceholder,
      required: true
    },
    {
      field: 'email',
      label: t.contact.steps.email,
      type: 'email',
      placeholder: t.contact.steps.emailPlaceholder,
      required: true
    },
    {
      field: 'company',
      label: t.contact.steps.company,
      type: 'text',
      placeholder: t.contact.steps.companyPlaceholder,
      required: false
    },
    {
      field: 'enquiryType',
      label: t.contact.steps.enquiryType,
      type: 'select',
      placeholder: t.contact.steps.enquiryPlaceholder,
      required: true,
      options: [
        { value: 'investor', label: t.contact.enquiryTypes.investor },
        { value: 'client', label: t.contact.enquiryTypes.client },
        { value: 'career', label: t.contact.enquiryTypes.career },
        { value: 'other', label: t.contact.enquiryTypes.other }
      ]
    },
    {
      field: 'message',
      label: t.contact.steps.message,
      type: 'textarea',
      placeholder: t.contact.steps.messagePlaceholder,
      required: true
    }
  ];

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError('');

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      setIsSubmitted(true);
      setFormData({ name: '', email: '', company: '', enquiryType: '', message: '' });
      setCurrentStep(0);
    } catch (error) {
      setSubmitError('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const nextStep = () => {
    const currentField = formSteps[currentStep];
    if (currentField.required && !formData[currentField.field]) {
      return;
    }
    if (currentStep < formSteps.length - 1) {
      setDirection('next');
      setCurrentStep(currentStep + 1);
    } else {
      handleSubmit();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setDirection('prev');
      setCurrentStep(currentStep - 1);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && formSteps[currentStep].type !== 'textarea') {
      e.preventDefault();
      nextStep();
    }
  };

  return (
    <div className="page-content">
      {/* Hero Section */}
      <section className="relative h-[40vh] min-h-[350px] overflow-hidden">
        <img src="/warehouse-aerial.jpg" alt="Contact" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-navy-dark/70 via-navy-primary/60 to-blue-accent/30"></div>

        <div className="absolute top-20 right-10 w-72 h-72 bg-gold-accent/10 rounded-full blur-3xl animate-pulse"></div>

        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white px-8 animate-fade-in-up">
            <span className="inline-block px-4 py-2 mb-6 text-sm font-medium tracking-widest uppercase bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
              {t.contact.heroTag}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-light mb-4 tracking-tight">
              {t.contact.heroTitle}
            </h1>
            <p className="text-xl font-light opacity-90 max-w-2xl mx-auto">
              {t.contact.heroSubtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute -top-40 -left-40 w-80 h-80 bg-blue-accent/5 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -right-40 w-80 h-80 bg-gold-accent/5 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-2xl mx-auto px-8 relative">
          {isSubmitted ? (
            <div className="bg-white shadow-2xl border border-gray-100 p-12 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-2xl font-semibold text-navy-primary mb-4">{t.contact.thankYou}</h3>
              <button
                onClick={() => setIsSubmitted(false)}
                className="mt-6 px-6 py-3 bg-gradient-to-r from-navy-primary to-navy-light text-white font-medium hover:shadow-xl transition-all duration-300"
              >
                Send Another Message
              </button>
            </div>
          ) : (
          <>
          <p className="text-center text-gray-600 text-lg mb-10">
            {t.contact.formIntro}
          </p>

          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex justify-between mb-2">
              {formSteps.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-300 ${
                    idx < currentStep
                      ? 'bg-blue-accent text-white'
                      : idx === currentStep
                        ? 'bg-navy-primary text-white scale-110'
                        : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {idx < currentStep ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    idx + 1
                  )}
                </div>
              ))}
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-navy-primary to-blue-accent transition-all duration-500 ease-out"
                style={{ width: `${((currentStep) / (formSteps.length - 1)) * 100}%` }}
              ></div>
            </div>
          </div>

          {/* Step Card */}
          <div className="bg-white shadow-2xl border border-gray-100 overflow-hidden">
            {/* Card Header */}
            <div className="px-8 pt-8 pb-6 border-b border-gray-100">
              <p className="text-gray-400 text-sm font-medium mb-1">Step {currentStep + 1} of {formSteps.length}</p>
              <h3 className="text-xl font-semibold text-navy-primary">{formSteps[currentStep].label}</h3>
            </div>

            {/* Card Body */}
            <div className="p-8">
              <div
                key={currentStep}
                className={`transition-all duration-300 ${
                  direction === 'next' ? 'animate-slide-in-right' : 'animate-slide-in-left'
                }`}
              >
                {formSteps[currentStep].type === 'select' ? (
                  <div className="space-y-3">
                    {formSteps[currentStep].options.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => {
                          setFormData({ ...formData, [formSteps[currentStep].field]: option.value });
                        }}
                        className={`w-full px-6 py-4 border-2 text-left transition-all duration-300 flex items-center justify-between group ${
                          formData[formSteps[currentStep].field] === option.value
                            ? 'border-blue-accent bg-blue-accent/5 text-navy-primary'
                            : 'border-gray-200 hover:border-blue-accent/50 text-gray-700'
                        }`}
                      >
                        <span className="font-medium">{option.label}</span>
                        {formData[formSteps[currentStep].field] === option.value && (
                          <svg className="w-5 h-5 text-blue-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </button>
                    ))}
                  </div>
                ) : formSteps[currentStep].type === 'textarea' ? (
                  <textarea
                    name={formSteps[currentStep].field}
                    value={formData[formSteps[currentStep].field]}
                    onChange={handleChange}
                    rows="5"
                    className="w-full px-5 py-4 border-2 border-gray-200 focus:border-blue-accent focus:outline-none transition-all duration-300 resize-none text-lg focus:shadow-lg focus:shadow-blue-accent/10"
                    placeholder={formSteps[currentStep].placeholder}
                    autoFocus
                  />
                ) : (
                  <input
                    type={formSteps[currentStep].type}
                    name={formSteps[currentStep].field}
                    value={formData[formSteps[currentStep].field]}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    className="w-full px-5 py-4 border-2 border-gray-200 focus:border-blue-accent focus:outline-none transition-all duration-300 text-lg focus:shadow-lg focus:shadow-blue-accent/10"
                    placeholder={formSteps[currentStep].placeholder}
                    autoFocus
                  />
                )}

                {formSteps[currentStep].required && !formData[formSteps[currentStep].field] && (
                  <p className="mt-3 text-sm text-gray-500 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {t.contact.steps.required}
                  </p>
                )}
              </div>

              {/* Navigation Buttons */}
              <div className="flex gap-4 mt-8">
                {currentStep > 0 && (
                  <button
                    type="button"
                    onClick={prevStep}
                    className="px-6 py-3 border-2 border-gray-200 text-gray-600 font-medium hover:border-navy-primary hover:text-navy-primary transition-all duration-300 flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    {t.contact.steps.back}
                  </button>
                )}
                <button
                  type="button"
                  onClick={nextStep}
                  disabled={(formSteps[currentStep].required && !formData[formSteps[currentStep].field]) || isSubmitting}
                  className={`flex-1 px-6 py-3 font-medium transition-all duration-300 flex items-center justify-center gap-2 group ${
                    (formSteps[currentStep].required && !formData[formSteps[currentStep].field]) || isSubmitting
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-navy-primary to-navy-light text-white hover:shadow-xl hover:shadow-navy-primary/20 hover:-translate-y-0.5'
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </>
                  ) : currentStep === formSteps.length - 1 ? (
                    <>
                      {t.contact.submit}
                      <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </>
                  ) : (
                    <>
                      {t.contact.continue}
                      <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </>
                  )}
                </button>
              </div>

              {submitError && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 text-red-700 text-sm">
                  {submitError}
                </div>
              )}
            </div>
          </div>
          </>
          )}
        </div>
      </section>
    </div>
  );
}

export default Contact;
